/* @flow */
import * as React from 'react';
import { graphql, compose } from 'react-apollo';

import ErrorActions from 'actions/ErrorActions';
import ToastActions from 'actions/ToastActions';
import LinkConstants from 'constants/LinkConstants';

import UsersProfile from './UsersProfile';
import UsersProfilePartial from './UsersProfilePartial';
import UPDATE_USER_NOTE_MUTATION from './queries/UpdateUserNoteMutation.gql';

import type { GroupProfileType } from 'groups/components/GroupProfile';
import type { UserProfile } from 'users/components/UsersProfile';

import type { UpdateUserNoteInput } from './queries/__generated__/updateUserNote';
import type { OrganizationMemberProfile_organizations_members_members } from './queries/__generated__/OrganizationMemberProfile';
import type { errorFragment } from 'queries/__generated__/errorFragment';

import './UsersProfile.scss';

export type Props = {
  entityUuid: string,
  member: UserProfile,
  loading: boolean,
  loaded: boolean,
  error: errorFragment,
  onBackClicked?: () => void,
  onGroupClicked: (group: GroupProfileType) => void,
  onFamilyMemberClicked: (user: UserProfile, role: string) => void,
  onClose: () => void,
  onUserChanged: () => void,
  reloadQuery: boolean,
  refetch: () => Promise<OrganizationMemberProfile_organizations_members_members>,
  mutate: ({
    variables: {
      input: UpdateUserNoteInput,
    },
  }) => Promise<*>,
  errorBanner: React.Element,
  userActionsMenuButton: React.Element,
  trackEvent: (string, ?Object) => void,
};

class UsersProfileContainer extends React.Component<Props> {
  componentDidUpdate(prevProps: Props) {
    const { reloadQuery, refetch, error, loading, member, onBackClicked, onClose } = this.props;

    if (reloadQuery && !prevProps.reloadQuery) {
      refetch();
    }

    if (error) {
      ErrorActions.serverError(error);
    }

    if (!loading && !member) {
      ToastActions.showErrorToast(
        `
          There was an error. Please try again, or email
          ${LinkConstants.SUPPORT_EMAIL}
          for help.
        `
      );

      if (onBackClicked) {
        onBackClicked();
      } else {
        onClose();
      }
    }
  }

  updateNote = (note: string) => {
    const { refetch, mutate, member, trackEvent } = this.props;

    trackEvent('user_profile.note.updated');

    mutate({
      variables: {
        input: {
          userId: member.id,
          note,
        },
      },
    })
      .then(() => {
        return refetch().catch(e => {
          console.debug(e, e && e.stack);
          console.error('UsersProfileContainer - failed to refresh sidebar profile');
          ToastActions.showErrorToast('Oops, an error occured, could not refresh profile');
        });
      })
      .catch(e => {
        console.debug(e, e && e.stack);
        console.error('UsersProfileContainer - failed to update note');
        ToastActions.showErrorToast('Oops, an error occured, could not update note');
      });
  };

  render() {
    const {
      errorBanner,
      member,
      loading,
      loaded,
      onBackClicked,
      onGroupClicked,
      onFamilyMemberClicked,
      onUserChanged,
      onClose,
      trackEvent,
      userActionsMenuButton,
    } = this.props;

    if (!member) {
      return null;
    }

    if (loading || !loaded) {
      return (
        <UsersProfilePartial
          loading={loading}
          loaded={loaded}
          member={member}
          onBackClicked={onBackClicked}
          onClose={onClose}
          errorBanner={errorBanner}
        />
      );
    } else {
      return (
        <UsersProfile
          member={member}
          onBackClicked={onBackClicked}
          onClose={onClose}
          onFamilyMemberClicked={onFamilyMemberClicked}
          onGroupClicked={onGroupClicked}
          onNoteChange={this.updateNote}
          onUserChanged={onUserChanged}
          trackEvent={trackEvent}
          userActionsMenuButton={userActionsMenuButton}
        />
      );
    }
  }
}

const withUpdateUserNoteMutation = graphql(UPDATE_USER_NOTE_MUTATION);

export { UsersProfileContainer };

export default compose(withUpdateUserNoteMutation)(UsersProfileContainer);
